import { makeAutoObservable } from 'mobx';
import { Utils } from '../utils/Utils';
import { ClosetMode } from '../config';
import { message } from 'antd';
import { Closet } from '../core/Closet';
import { Unit } from '../core/Unit';

export class MatrixManager {
    _libState = null;
    _pdfFile = null;
    _pdfCanvasRef = null;
    _pdfDoc = null;
    _scaleOnPdf = 1;
    _selectedCloset = null;
    _closets = [];
    _attachments = [];
    _obstacles = [];
    _units = [];
    _annotationCanvas = null;
    _closetCanvas = null;
    _scaleForAnnotation = 1;
    _scaleFactorOnPdfPage = 0.25;
    _tolForDelete = 10;
    _closetMode = ClosetMode.EDIT;
    _attachmentEditMode = false;
    _scaleForZoom = 1;
    _enableZoom = true;
    _panMode = false;
    _selectedPageNo = 1;
    _selectedPagesDetails = [];
    _coordinatesToSave = null;
    _showSnapPoints = false;
    _selectedEdgeEnds = [];
    _isSaving = false;
    _selectedAttachment = null;
    _translateX = 0;
    _translateY = 0;
    _start = { x: 0, y: 0 };
    _last = { x: 0, y: 0 };
    _divRef = null;
    _obstacleEditMode = false;
    _obstacleForm = false;
    _selectedClosetId = null;
    _obstacleCoords = null;
    _selectedObstacle = null;
    _selectedUnit = null;
    _enableEditObstacleForm = false;
    _obstaclePosData = {};
    _scaleForFit = 1;
    _presentUnits = [];
    _allUnitData = null;
    _isLoading = false;
    _canCalibrate = false;
    _calibrationData = [];
    _pages = 0;
    _matrixData = [];
    _tablePosition = { x: 20, y: 10 };
    _resetTablePositionTriggered = false;

    constructor(libState) {
        makeAutoObservable(this);
        this._libState = libState;
    }

    get pdfBuffer() {
        return this._pdfFile;
    }

    get selectedPageNo() {
        return this._selectedPageNo;
    }

    get pdfCanvasRef() {
        return this._pdfCanvasRef;
    }
    get closetCanvas() {
        return this._closetCanvas;
    }

    get annotationCanvas() {
        return this._annotationCanvas;
    }

    get pdfDoc() {
        return this._pdfDoc;
    }

    get panMode() {
        return this._panMode;
    }

    get scaleForZoom() {
        return this._scaleForZoom;
    }

    setScaleForZoom(scaleForZoom) {
        this._scaleForZoom = scaleForZoom;
    }

    get isSaving() {
        return this._isSaving;
    }
    get divRef() {
        return this._divRef;
    }

    get scaleForFit() {
        return this._scaleForFit;
    }

    get isLoading() {
        return this._isLoading;
    }
    get pages() {
        return this._pages;
    }

    get matrixData() {
        return this._matrixData;
    }

    get tablePosition() {
        return this._tablePosition;
    }

    get resetTablePositionTriggered() {
        return this._resetTablePositionTriggered;
    }

    get units() {
        return this._units;
    }

    get closets() {
        return this._closets;
    }

    setMatrixData(matrixData) {
        this._matrixData = matrixData;
    }

    setPages(pages) {
        this._pages = pages;
    }

    setIsLoading(isLoading) {
        this._isLoading = isLoading;
    }

    setScaleForFit(scaleForFit) {
        this._scaleForFit = scaleForFit;
    }

    setDivRef(divRef) {
        this._divRef = divRef;
    }

    setIsSaving(isSaving) {
        this._isSaving = isSaving;
    }
    setStart(x, y) {
        this._start.x = x;
        this._start.y = y;
    }
    setLast(x, y) {
        this._last.x = x;
        this._last.y = y;
    }

    get start() {
        return this._start;
    }

    get last() {
        return this._last;
    }
    get selectedAttachment() {
        return this._selectedAttachment;
    }

    setPanMode(inBool) {
        this._panMode = inBool;
    }

    get scaleOnPdf() {
        return this._scaleOnPdf;
    }

    get pdfCanvasContext() {
        return this._pdfCanvasRef.getContext('2d');
    }

    get scaleFactorOnPdfPage() {
        return this._scaleFactorOnPdfPage;
    }

    get enableZoom() {
        return this._enableZoom;
    }

    get closetCanvasRect() {
        const closetCanvasRect = this.closetCanvas.getBoundingClientRect();
        return closetCanvasRect;
    }

    get scaleFactorOnPdfInFraction() {
        return this._scaleFactorOnPdfPage;
    }

    get translateX() {
        return this._translateX;
    }

    get translateY() {
        return this._translateY;
    }

    setTranslateX(translateX) {
        this._translateX = translateX;
    }

    setTranslateY(translateY) {
        this._translateY = translateY;
    }

    setAttachmentEditMode(bool) {
        this._attachmentEditMode = bool;
    }
    setSelectedAttachment(selectedAttachment) {
        this._selectedAttachment = selectedAttachment;
    }

    async setSelectedPageNo(selectedPageNo) {
        this._selectedPageNo = selectedPageNo;
        await this.renderPage();
        this.setIsLoading(true);
        await this.fitToScreen();
        this.setIsLoading(false);
    }

    setScaleFactorByPageNo(pageNo, scaleFactor) {
        const selectedPageDetails = this._selectedPagesDetails.find(
            (page) => page.pageNo == pageNo,
        );
        if (selectedPageDetails) {
            selectedPageDetails.scaleFactor = scaleFactor;
            this._selectedPagesDetails = [...this._selectedPagesDetails];
        }
    }

    fetchScaleFactorByPageNo(pageNo) {
        const selectedPageDetails = this._selectedPagesDetails.find(
            (page) => page.pageNo == pageNo,
        );
        if (selectedPageDetails) {
            return selectedPageDetails.scaleFactor;
        } else {
            this._selectedPagesDetails.push({
                pageNo,
                scaleFactor: 1,
            });
            return 1;
        }
    }
    setDeleteMode() {
        this._closetMode =
            this._closetMode === ClosetMode.DELETE
                ? ClosetMode.EDIT
                : ClosetMode.DELETE;
        if (this._closetMode === ClosetMode.DELETE) {
            this.addMinusButton();
            this.addDeletePolygonButton();
        }
        if (this._closetMode === ClosetMode.EDIT) {
            this.drawNewClosets();
        }
    }
    setObstacleMode(bool) {
        if (bool) {
            this._closetMode = ClosetMode.OBSTACLE;
        } else {
            this._closetMode = ClosetMode.EDIT;
        }
    }
    setNoneMode() {
        this._closetMode =
            this._closetMode === ClosetMode.NONE
                ? ClosetMode.EDIT
                : ClosetMode.NONE;
    }

    setEditMode() {
        this._closetMode = ClosetMode.EDIT;
    }

    setEnableZoom(enableZoom) {
        this._enableZoom = enableZoom;
    }

    setAttachmentAttributes(attachmentType, type, openingType) {
        const lastAttachment = this.attachments[this.attachments.length - 1];
        if (lastAttachment) {
            lastAttachment.setAttachmentType(attachmentType);
            lastAttachment.setType(type);
            lastAttachment.setOpening(openingType);
        }
        this._attachments = [...this._attachments];
    }

    updateAttachmentAttributes(attachmentType, type, openingType) {
        const attachment = this.attachments.find(
            (attachment) => attachment.id === this.selectedAttachment.id,
        );
        if (attachment) {
            attachment.setAttachmentType(attachmentType);
            attachment.setType(type);
            attachment.setOpening(openingType);
            this.setSelectedAttachment(null);
        }
        this._attachments = [...this._attachments];
    }

    setTolForDelete = (tolForDelete) => {
        this._tolForDelete = tolForDelete;
    };

    setScaleFactorOnPdfPage = (scaleFactorOnPdfPage) => {
        this._scaleFactorOnPdfPage = scaleFactorOnPdfPage;
    };

    setScaleOnChange(scaleOnPdf) {
        this._selectedPagesDetails.find(
            (page) => page.pageNo === this.selectedPageNo,
        ).scaleFactor = scaleOnPdf;
    }

    setPdfBuffer = (pdfBuffer) => {
        this._pdfFile = pdfBuffer;
    };

    setPdfCanvasRef = (canvasRef) => {
        this._pdfCanvasRef = canvasRef;
    };

    setAnnotationCanvas = (annotationCanvas) => {
        this._annotationCanvas = annotationCanvas;
    };

    setClosetCanvas = (closetCanvas) => {
        this._closetCanvas = closetCanvas;
    };

    setPdfDoc = (pdfDoc) => {
        this._pdfDoc = pdfDoc;
    };

    setScaleOnPdf = (scaleOnPdf) => {
        this._scaleOnPdf = scaleOnPdf;
    };

    setTablePosition(position) {
        this._tablePosition = position;
    }

    resetUnitTablePosition() {
        this._resetTablePositionTriggered = true;
    }

    clearResetTablePosition() {
        this._resetTablePositionTriggered = false;
    }

    zoomIn(x, y) {
        const mouseX = x;
        const mouseY = y;
        if (this.panMode) return;
        if (this._enableZoom) {
            let scaleFactor = this.fetchScaleFactorByPageNo(
                this.selectedPageNo,
            );
            const xTrans =
                this.translateX -
                (mouseX - this.translateX) *
                    ((scaleFactor + 0.1) / scaleFactor - 1);
            const yTrans =
                this.translateY -
                (mouseY - this.translateY) *
                    ((scaleFactor + 0.1) / scaleFactor - 1);
            this.setTranslateX(xTrans);
            this.setTranslateY(yTrans);
            this._scaleForZoom = scaleFactor / (scaleFactor + 0.1);
            scaleFactor += 0.1;

            this.setScaleFactorByPageNo(this.selectedPageNo, scaleFactor);
            this.clearClosetCanvas();
            this.renderPage();
        }
    }

    async zoomForFit(scale) {
        const mouseX = 0;
        const mouseY = 0;
        let scaleFactor = this.fetchScaleFactorByPageNo(this.selectedPageNo);
        const xTrans =
            this.translateX -
            (mouseX - this.translateX) * (scale / scaleFactor - 1);
        const yTrans =
            this.translateY -
            (mouseY - this.translateY) * (scale / scaleFactor - 1);
        this.setTranslateX(xTrans);
        this.setTranslateY(yTrans);
        this._scaleForZoom = scaleFactor / scale;
        scaleFactor = scale;

        this.setScaleFactorByPageNo(this.selectedPageNo, scaleFactor);
        if (
            this._libState.drawingManager.isDrawingModeOn ||
            this._libState.drawingManager.isDoorModeOn ||
            this._libState.drawingManager.isUnitModeOn
        ) {
            if (this._libState.drawingManager.closetPolygon.length > 0) {
                this._libState.drawingManager.scaleDrawnPolygon(
                    this._scaleForZoom,
                );
            }
        }
        this.clearClosetCanvas();
        this.renderPage();
    }

    calculatePanRendering(x, y) {
        const xTrans = this.last.x + (x - this.start.x);
        const yTrans = this.last.y + (y - this.start.y);
        this.setTranslateX(xTrans);
        this.setTranslateY(yTrans);

        this.clearClosetCanvas();
        this.renderPage();
    }
    zoomOut(x, y) {
        const mouseX = x;
        const mouseY = y;
        if (this.panMode) return;
        if (this._enableZoom) {
            let scaleFactor = this.fetchScaleFactorByPageNo(
                this.selectedPageNo,
            );
            if (scaleFactor < this.scaleForFit) return;
            const xTrans =
                this.translateX -
                (mouseX - this.translateX) *
                    ((scaleFactor - 0.1) / scaleFactor - 1);
            const yTrans =
                this.translateY -
                (mouseY - this.translateY) *
                    ((scaleFactor - 0.1) / scaleFactor - 1);
            this.setTranslateX(xTrans);
            this.setTranslateY(yTrans);
            this._scaleForZoom = scaleFactor / (scaleFactor - 0.1);
            scaleFactor -= 0.1;

            this.setScaleFactorByPageNo(this.selectedPageNo, scaleFactor);
            if (
                this._libState.drawingManager.isDrawingModeOn ||
                this._libState.drawingManager.isDoorModeOn ||
                this._libState.drawingManager.isUnitModeOn ||
                this._libState.drawingManager.isCalibrationModeOn
            ) {
                if (this._libState.drawingManager.closetPolygon.length > 0) {
                    this._libState.drawingManager.scaleDrawnPolygon(
                        this._scaleForZoom,
                    );
                }
            }
            this.clearClosetCanvas();
            this.renderPage();
        }
    }

    async fitToScreen() {
        const closetCanvasRect = this.pdfCanvasRef.getBoundingClientRect();
        const containerWidth = closetCanvasRect.width;
        const containerHeight = closetCanvasRect.height;
        const pageNo = this.selectedPageNo;

        if (!this.pdfDoc) {
            const pdfDoc = await Utils.readPdf(this.pdfBuffer);
            this.setPdfDoc(pdfDoc);
        }

        const page = await this.pdfDoc.getPage(pageNo);
        const viewport = page.getViewport({ scale: 1 });
        const contentWidth = viewport.width;
        const contentHeight = viewport.height;

        const scaleX = containerWidth / contentWidth;
        const scaleY = containerHeight / contentHeight;
        const scaleFactor = Math.min(scaleX, scaleY);

        const contentCenterX = (contentWidth / 2) * scaleFactor;
        const contentCenterY = (contentHeight / 2) * scaleFactor;

        const viewportCenterX = containerWidth / 2;
        const viewportCenterY = containerHeight / 2;

        const transX = viewportCenterX - contentCenterX;
        const transY = viewportCenterY - contentCenterY;

        this.setScaleForFit(scaleFactor);
        await this.zoomForFit(scaleFactor);
        this.setTranslateX(transX);
        this.setTranslateY(transY);
        this.setScaleFactorByPageNo(pageNo, scaleFactor);

        this.clearClosetCanvas();
    }

    clearClosetCanvas() {
        if (!this.closetCanvas) return;
        this.closetCanvas
            .getContext('2d')
            .clearRect(0, 0, this.closetCanvas.width, this.closetCanvas.height);
    }

    async renderPage() {
        if (!this.pdfDoc) {
            const pdfDoc = await Utils.readPdf(this.pdfBuffer);
            this.setPdfDoc(pdfDoc);
        }

        if (!this.pdfBuffer) throw new Error('No PDF file selected.');

        if (this.pages == 0) {
            this.setPages(this.pdfDoc.numPages);
        }

        const pageNo = this.selectedPageNo;
        const page = await this.pdfDoc.getPage(pageNo);
        const viewport = page.getViewport({
            scale: this.fetchScaleFactorByPageNo(pageNo),
        });

        if (
            !this.divRef ||
            !this.divRef.clientWidth ||
            !this.divRef.clientHeight
        )
            this.setDivRef(this._libState.closetManager.divRef);

        this.pdfCanvasRef.width = this.divRef.clientWidth;
        this.pdfCanvasRef.height = this.divRef.clientHeight;

        const ctx = this.pdfCanvasRef.getContext('2d');

        if (this.renderTask) {
            await this.renderTask.cancel();
        }

        ctx.clearRect(0, 0, this.pdfCanvasRef.width, this.pdfCanvasRef.height);
        ctx.save();
        ctx.translate(this.translateX, this.translateY);
        this.renderTask = page.render({ canvasContext: ctx, viewport });
        try {
            await this.renderTask.promise;
            ctx.restore();
        } catch (error) {
            if (error.name !== 'RenderingCancelledException') {
                if (!error.message.includes('multiple render operations')) {
                    console.error('Render failed:', error);
                }
            }
        }
    }

    extractTextAndExport = async (pdfDoc, PAGE_NO) => {
        try {
            const page = await pdfDoc.getPage(PAGE_NO);
            const textContent = await page.getTextContent();

            const text = textContent.items.map((item) => item.str).join('\n');
            const scaleIndex = text.indexOf('SCALE:');
            if (scaleIndex === -1) {
                throw new Error('SCALE: not found');
            }

            const scaleText = text
                .substring(scaleIndex + 7, scaleIndex + 20)
                .trim();

            const fractionMatch = scaleText.match(/(\d+)\/(\d+)/);
            if (fractionMatch) {
                const numerator = parseInt(fractionMatch[1], 10);
                const denominator = parseInt(fractionMatch[2], 10);
                const decimalValue = numerator / denominator;

                if (!this.scaleFactorOnPdfPage)
                    this.setScaleFactorOnPdfPage(decimalValue);
            } else {
                console.warn('No fraction found in scale');
            }
        } catch (error) {
            console.error('Error extracting text:', error);
        }
    };

    stopZoom = () => {
        this.setEnableZoom(!this.enableZoom);
    };
    setZoom(inBool) {
        this.setEnableZoom(inBool);
    }
    setShowSnapPoints(showSnapPoints) {
        this._showSnapPoints = showSnapPoints;
    }

    setUnitData(units) {
        this._units = [];
        for (const unit of units) {
            this._units.push(
                new Unit(unit._id, unit._pageNo, unit._name, unit._scaleOnPdf),
            );
        }
    }

    setClosetData(closets) {
        this._closets = [];
        for (const closet of closets) {
            const closetData = new Closet(
                closet._id,
                closet._pageNo,
                closet._scaleOnPdf,
            );
            closetData.setName(closet._name);
            closetData.setUnitId(closet._unitId);
            closetData.setAllCoordinates(closet._coordinates);
            this._closets.push(closetData);
        }
    }

    processUnitData() {
        const unitsByName = {};
        this._units.forEach((unit) => {
            if (!unitsByName[unit.name]) {
                unitsByName[unit.name] = [];
            }
            unitsByName[unit.name].push(unit);
        });

        Object.entries(unitsByName).forEach(([name, units]) => {
            if (units.length > 1) {
                const primaryUnitId = units[0].id;

                this._closets.forEach((closet) => {
                    units.slice(1).forEach((duplicateUnit) => {
                        if (closet.unitId === duplicateUnit.id) {
                            closet._unitId = primaryUnitId;
                        }
                    });
                });
                this._units = this._units.filter(
                    (unit) => unit.name !== name || unit.id === primaryUnitId,
                );
            }
        });
        this._units = this._units.filter(
            (unit, index, self) =>
                self.findIndex((u) => u.id === unit.id) === index,
        );
    }

    downloadCSV() {
        const validRows = this._matrixData.filter(
            (row) => row.type && row.type !== 'Select Unit',
        );

        if (validRows.length === 0) {
            message.warning('No valid entries to download');
            return;
        }

        // Convert to CSV format
        const headers = ['Unit Number', 'Type', 'Floor'];
        const csvContent = [
            headers.join(','),
            ...validRows.map((row) =>
                [row.unitNumber || '', row.type || '', row.floor || ''].join(
                    ',',
                ),
            ),
        ].join('\n');

        // Create and download the file
        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'unit_table.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}
